import React, { useState } from 'react';
import { useEsMovil } from '../../indicadores/hooks/useEsMovil';
import type { PendienteDeTienda } from '../types/metas.types';
import { nombrePropio } from '../utils/formato';
import { separarPendientes } from '../utils/pendientes';
import { cx } from './ui/clases';

interface Props {
  pendientes: readonly PendienteDeTienda[] | null | undefined;
  /** Enlace a la pestaña «Pendientes de catálogo» (o nada si la cuenta no la ve). */
  onVerPendientes?: () => void;
  className?: string;
}

const IconoCandado = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-px shrink-0">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const IconoInfo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-px shrink-0">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </svg>
);

/** «Colaborador en dos tiendas · Raúl Mendoza Cid: …» (el nombre, legible). */
const Renglon: React.FC<{ p: PendienteDeTienda }> = ({ p }) => (
  <li className="[overflow-wrap:anywhere]">
    <b className="font-semibold">{p.etiqueta}</b>
    {' · '}
    {p.colaboradorKey ? nombrePropio(p.referencia) : p.referencia}: {p.detalle}{' '}
    <span className="opacity-80">(lo corrige {p.area})</span>
  </li>
);

/**
 * Los pendientes de catálogo de ESTA tienda en la Captura (fase 4), evaluados
 * en vivo por la API con cada vista y cada guardado.
 *
 * - Los que BLOQUEAN el envío (por omisión «Colaborador en dos tiendas» y
 *   «Tienda sin meta de tienda») van en rojo, con candado y texto.
 * - Los informativos van aparte, en ámbar: no impiden enviar, pero el
 *   regional los verá como alerta y las comisiones pueden salir mal.
 *
 * No se corrigen aquí: en su sistema de origen. La excepción es «en dos
 * tiendas», que se resuelve marcando «Tienda errónea» (o «Apoyo en otra tienda»)
 * en la tienda donde la persona no trabaja.
 */
export const BannerPendientesCatalogo: React.FC<Props> = ({ pendientes, onVerPendientes, className }) => {
  // Hasta 1023 px lo informativo va plegado en un renglón: en el teléfono ocupaba
  // la primera pantalla y no se veía ningún colaborador (auditoría del 22-oct-2026).
  // Lo que bloquea el envío nunca se pliega.
  const plegable = useEsMovil(1023);
  const [desplegado, setDesplegado] = useState(false);
  const { bloquean, informan } = separarPendientes(pendientes);
  if (!bloquean.length && !informan.length) return null;
  const verInforman = !plegable || desplegado;
  const enlace = onVerPendientes && (
    // 44 px de alto en teléfono o pantalla táctil (medía 196 × 20; auditoría del 22-oct-2026).
    <button
      type="button"
      className="metas-foco inline-flex items-center self-start text-[13px] font-semibold text-metas-primario underline max-md:min-h-metas-tactil [@media(pointer:coarse)]:min-h-metas-tactil"
      onClick={onVerPendientes}
    >
      Ver en «Pendientes de catálogo»
    </button>
  );
  return (
    <div className={cx('flex flex-col gap-2', className)} data-testid="banner-pendientes-catalogo">
      {bloquean.length > 0 && (
        <div
          className="flex items-start gap-3 rounded-metas-panel border border-metas-error bg-metas-error-fondo px-4 py-3 text-sm text-metas-error"
          role="alert"
          data-testid="pendientes-bloquean"
        >
          <IconoCandado />
          <div className="flex min-w-0 flex-col gap-1">
            <p className="m-0 font-semibold">
              {bloquean.length === 1
                ? 'Un pendiente de catálogo impide enviar esta tienda:'
                : `${bloquean.length} pendientes de catálogo impiden enviar esta tienda:`}
            </p>
            <ul className="m-0 flex list-disc flex-col gap-0.5 pl-5">
              {bloquean.map((p, i) => (
                <Renglon key={`${p.tipo}-${p.colaboradorKey ?? 'tienda'}-${i}`} p={p} />
              ))}
            </ul>
            {enlace}
          </div>
        </div>
      )}
      {informan.length > 0 && (
        <div
          className="flex items-start gap-3 rounded-metas-panel border border-metas-banner-borde bg-metas-banner px-4 py-3 text-sm text-metas-banner-texto"
          role="status"
          data-testid="pendientes-informan"
        >
          <IconoInfo />
          <div className="flex min-w-0 flex-col gap-1">
            {plegable ? (
              <button
                type="button"
                className="metas-foco -my-1 inline-flex min-h-metas-tactil items-center gap-2 self-start text-left font-semibold"
                aria-expanded={desplegado}
                onClick={() => setDesplegado((v) => !v)}
              >
                Datos de catálogo por corregir ({informan.length}, no impiden enviar)
                <i className={cx('pi text-xs', desplegado ? 'pi-chevron-up' : 'pi-chevron-down')} aria-hidden="true" />
              </button>
            ) : (
              <p className="m-0 font-semibold">Datos de catálogo por corregir (no impiden enviar):</p>
            )}
            {verInforman && (
              <ul className="m-0 flex list-disc flex-col gap-0.5 pl-5">
                {informan.map((p, i) => (
                  <Renglon key={`${p.tipo}-${p.colaboradorKey ?? 'tienda'}-${i}`} p={p} />
                ))}
              </ul>
            )}
            {!bloquean.length && verInforman && enlace}
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerPendientesCatalogo;
